/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { ReactNode, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useMutation } from '@tanstack/react-query'
import { Alert, Button, Modal, ModalProps, PinInput, Stack, Text } from '@mantine/core'
import ButtonGroup from '../form/ButtonGroup'

export type DeleteSelectionModalProps<T> = ModalProps & {
  items: T[],
  onConfirm (value: T): Promise<unknown>,
  onSuccess? (): void,
  requirePin?: boolean,
  success?: ReactNode,
}

function DeleteSelectionModal<T> (props: DeleteSelectionModalProps<T>) {
  const {
    children,
    items,
    opened,
    onClose,
    onConfirm,
    onSuccess,
    requirePin,
    success,
    ...others
  } = props

  const intl = useIntl()

  const pin = useMemo(() => String(Math.round(Math.random() * 9999)).padStart(4, '0'), [])

  const deleter = useMutation<unknown, Error>({
    mutationKey: ['delete', onConfirm],
    mutationFn: async () => {
      const promises = Promise.all(items.map(onConfirm))

      if (onSuccess) {
        promises.then(onSuccess)
      }
      return promises
    }
  })

  const [confirmation, setConfirmation] = useState<string>(null)

  return (
    <Modal
      centered
      title={<FormattedMessage
        id="delete"
        defaultMessage="Supprimer"
      />}
      {...others}
      onClose={onClose}
      opened={opened}
    >
      <Stack>
        {deleter.isSuccess
          ? <Text>
            <FormattedMessage
              id="delete_succeeded"
              defaultMessage="La suppression a été effectuée."
            />
            {success}
          </Text>
          : <>
            <Text>
              {items.length > 1
                ? <FormattedMessage
                  id="do_you_want_to_delete_selected_items"
                  defaultMessage="Êtes-vous sûr de vouloir supprimer {count} éléments ?"
                  values={{ count: <b>{items.length}</b> }}
                />
                : <FormattedMessage
                  id="do_you_want_to_delete_the_item"
                  defaultMessage="Êtes-vous sûr de vouloir supprimer {count} élément ?"
                  values={{ count: <b>1</b> }}
                />
              }
            </Text>

            {children != null
              ? <fieldset>
                {children}
              </fieldset>
              : null}

            {requirePin
              ? <Stack>
                <Text>
                  {intl.formatMessage({
                    id: 'please_type_confirmation_text_to_continue',
                    defaultMessage: 'Veuillez saisir "{text}" dans le champ suivant :'
                  }, { text: <code>{pin}</code> })}
                </Text>
                <PinInput
                  mx="auto"
                  length={pin.length}
                  onChange={setConfirmation}
                  value={confirmation || ''}
                />
              </Stack>
              : null}
          </>
        }

        {deleter.error != null
          ? <Alert color="red">
            {deleter.error?.message}
          </Alert>
          : null}

        <ButtonGroup>
          <Button
            disabled={deleter.isPending}
            onClick={onClose}
          >
            {deleter.isSuccess
              ? <FormattedMessage
                id="close"
                defaultMessage="Fermer"
              />
              : <FormattedMessage
                id="cancel"
                defaultMessage="Annuler"
              />
            }
          </Button>
          <Button
            variant="filled"
            color={deleter.isSuccess ? 'green' : undefined}
            disabled={deleter.isPending || deleter.isSuccess ||
              (requirePin && confirmation !== pin)}
            loading={deleter.isPending}
            onClick={() => {
              deleter.mutate()
            }}
          >
            <FormattedMessage
              id="delete"
              defaultMessage="Supprimer"
            />
          </Button>
        </ButtonGroup>
      </Stack>
    </Modal>
  )
}

export default DeleteSelectionModal
